import { useState } from "react";

import BaseCard from "../../../../../components/ui/BaseCard";
import BaseConfirmModal from "../../../../../components/ui/BaseConfirmModal";
import AddLicenseAndCertificationModal from "../../../components/AddLicenseAndCertificationModal";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { deleteUserQualification, fetchUserQualificationsByType } from "../../../../../services/api/user";
import { showToast } from "../../../../../store/toast/toastSlice";

const AddLicenseCertifications = ({ userId, licencesId, certificationId }) => {
  const dispatch = useDispatch();

  const [isAddLicenseAndCertificationModalOpen, setIsAddLicenseAndCertificationModalOpen] =
    useState(false);
  const [selectedUserCertificate, setSelectedUserCertificate] = useState({
    qualificationSubTypeId: null,
    startDate: null,
    endDate: null,
  });
  const [certificateToDelete, setCertificateToDelete] = useState(null);

  const isEdit = selectedUserCertificate?.qualificationSubTypeId ? true : false;

  const {
    data: certifications,
    refetch: refetchUserQualificationsByType,
  } = useQuery({
    queryKey: ["certifications", userId],

    queryFn: async () => {
      const [certificationSubs, licenseSubs] = await Promise.all([
        certificationId ? fetchUserQualificationsByType(userId, certificationId) : [],
        licencesId ? fetchUserQualificationsByType(userId, licencesId) : [],
      ]);

      // Guard: a skipped fetch returns [] whose `.data` is undefined.
      return [...(certificationSubs?.data || []), ...(licenseSubs?.data || [])];
    },
    enabled: (!!licencesId || !!certificationId) && !!userId,
  });

  const { isPending: isDeleting, mutateAsync: deleteCertificateMutation } = useMutation({
    mutationFn: (id) => deleteUserQualification(id, userId),
    enabled: false,
  });

  const handleAddClick = () => {
    setSelectedUserCertificate({ qualificationSubTypeId: null, startDate: null, endDate: null });
    setIsAddLicenseAndCertificationModalOpen(true);
  };

  const handleEditCertificate = (certificate) => {
    setSelectedUserCertificate(certificate);
    setIsAddLicenseAndCertificationModalOpen(true);
  };

  const handleDeleteCertificate = async () => {
    try {
      await deleteCertificateMutation(certificateToDelete.id);
      await refetchUserQualificationsByType();
      dispatch(showToast({ message: "Certification deleted successfully", type: "success" }));
      setCertificateToDelete(null);
    } catch (error) {
      dispatch(showToast({ message: "Failed to delete certification", type: "error" }));
    }
  };

  return (
    <form>
      <BaseCard className="py-3 px-4 mt-3 border rounded-lg shadow-sm" elevation={0}>
        <div className="bg-light rounded-sm py-2 px-4 mb-4 flex items-center justify-between">
          <p className="text-base font-normal">Add License & Certifications</p>
          <i
            className="fa-solid fa-circle-plus cursor-pointer text-secondary text-xl"
            onClick={handleAddClick}
          ></i>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          {certifications?.map((certification) => {
            return (
              <div
                className="bg-light rounded-md py-3 px-4 flex justify-between gap-3 border"
                key={certification.id}
              >
                <div className="min-w-0">
                  <p className="text-base font-medium break-words">
                    {certification?.qualificationSubType}
                  </p>
                  <div className="flex flex-wrap gap-x-6 mt-2">
                    <div className="flex">
                      <p className="font-medium">Start Date: </p>
                      <p className="ml-2">
                        {dayjs(certification?.startDate).format("DD/MM/YYYY")}
                      </p>
                    </div>
                    <div className="flex">
                      <p className="font-medium">End Date: </p>
                      <p className="ml-2">{dayjs(certification?.endDate).format("DD/MM/YYYY")}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start shrink-0">
                  <i
                    className="fa-regular fa-trash-can text-red-600 cursor-pointer mr-3"
                    title="Delete"
                    onClick={() => setCertificateToDelete(certification)}
                  ></i>
                  <i
                    className="fa-light fa-pencil text-gray-900 cursor-pointer"
                    title="Edit"
                    onClick={() => handleEditCertificate(certification)}
                  ></i>
                </div>
              </div>
            );
          })}
        </div>
      </BaseCard>

      {isAddLicenseAndCertificationModalOpen && (
        <AddLicenseAndCertificationModal
          open={isAddLicenseAndCertificationModalOpen}
          close={setIsAddLicenseAndCertificationModalOpen}
          refetchUserQualificationsByType={refetchUserQualificationsByType}
          licencesId={licencesId}
          certificationId={certificationId}
          isEdit={isEdit}
          setSelectedUserCertificate={setSelectedUserCertificate}
          selectedUserCertificate={selectedUserCertificate}
          qualificationSubTypeId={null}
          targetUserId={userId}
        />
      )}

      {certificateToDelete && (
        <BaseConfirmModal
          open={!!certificateToDelete}
          onClose={() => setCertificateToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Certification"
          description="Are you sure you want to delete this license/certification? This action cannot be undone."
          isLoading={isDeleting}
          onConfirm={handleDeleteCertificate}
        />
      )}
    </form>
  );
};

export default AddLicenseCertifications;
